import { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, ChevronDown } from "lucide-react";
import { getSessionToken } from "@/utils/auth";

const API = `${window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${getSessionToken()}` } });

// Inline warning under the tech pickers when a tech isn't checked for something on the job.
export const TechCapabilityWarning = ({ techNames, lineItems }) => {
  const [results, setResults] = useState([]);
  const names = techNames.filter(Boolean);
  const items = (lineItems || []).map((li) => li?.name).filter(Boolean);
  const key = `${names.join("|")}::${items.join("|")}`;

  useEffect(() => {
    if (!names.length || !items.length) { setResults([]); return; }
    axios.post(`${API}/techs/capability-check`, { tech_names: names, line_items: items }, auth())
      .then((r) => setResults(r.data.results.filter((x) => x.missing.length)))
      .catch(() => setResults([]));
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!results.length) return null;
  return (
    <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3 text-sm space-y-1" data-testid="tech-capability-warning">
      {results.map((r) => (
        <div key={r.tech_name} className="flex gap-2 text-amber-900">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
          <span><strong>{r.tech_name}</strong> isn't set up for: {r.missing.join(", ")}. You can still save.</span>
        </div>
      ))}
    </div>
  );
};

// Banner on the Techs page listing upcoming jobs already assigned to an unqualified tech.
export const CapabilityAuditBanner = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    axios.get(`${API}/techs/capability-audit`, auth()).then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data?.count) return null;
  return (
    <div className="mb-4 rounded-xl border-2 border-amber-300 bg-amber-50" data-testid="capability-audit-banner">
      <button onClick={() => setOpen(!open)} className="w-full p-3 flex items-center gap-2 text-left" data-testid="capability-audit-toggle">
        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
        <span className="flex-1 text-sm font-semibold text-amber-900">
          {data.count} upcoming job{data.count === 1 ? "" : "s"} assigned to a tech who isn't set up for the work
        </span>
        <ChevronDown className={`w-4 h-4 text-amber-700 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          {data.jobs.map((j, i) => (
            <a key={`${j.job_id}-${i}`} href={`/jobs?job=${j.job_id}`}
              className="block bg-white rounded-lg border border-amber-200 p-2 text-xs" data-testid={`capability-audit-job-${j.job_id}`}>
              <div className="flex justify-between font-semibold text-zinc-900">
                <span>{j.customer_name || "Customer"}</span>
                <span>{j.date}{j.time ? ` · ${j.time}` : ""}</span>
              </div>
              <div className="text-zinc-600 mt-0.5">{j.tech_name} is missing: {j.missing.join(", ")}</div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
};
